// RADIUS data — the read paths the subscriber-detail screen needs.
//
//   GET /v1/api/radius/details?username=…  — pure DB read of the synced
//        radius_subscribers row (package, switch, mac, ip, expiry,
//        account status, last_logoff, last_synced). 404 → not in the
//        local mirror.
//
// (Billing/ledger and the canonical /v1/api/radius_status/run action
//  surface come later — Phase 2 detail is read-only DB data.)

import type { ApiClient } from "./client.js";
import type { RadiusDetailsResponse } from "./types.js";

/** GET /v1/api/radius/live — one live-portal hit + DB upsert, so the next
 *  DB-backed read (e.g. /v1/api/switches/users) sees fresh data without
 *  waiting for the 3-min radiussync ticker. (We only care about `ok` at
 *  the call sites; the full response also carries `row` + timings.) */
export interface RadiusLiveResult {
  ok: boolean;
  error?: string;
}

export function makeRadiusApi(client: ApiClient) {
  return {
    /** DB row for a username from the synced radius_subscribers mirror. */
    details(username: string): Promise<RadiusDetailsResponse> {
      return client.get<RadiusDetailsResponse>("/v1/api/radius/details", { username });
    },
    /** Force a live-portal re-sync of one subscriber's row into the local
     *  mirror. Used after a write (Edit / Send-to-Close) so the next
     *  DB-backed list reflects the change immediately. ~1-3s. */
    live(username: string): Promise<RadiusLiveResult> {
      return client.get<RadiusLiveResult>("/v1/api/radius/live", { username });
    },
  };
}

export type RadiusApi = ReturnType<typeof makeRadiusApi>;
